import React, { useCallback, useEffect, useState } from 'react';
import {
  PageHeader, DataTable, Tag, Button, Dialog, ConfirmDialog, Field, TextInput, Switch, useCkToast,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import api from '../../services/api.js';
import { useQuickCreate } from '../../next/quickCreate';
import { FormGrid, apiError } from '../../next/ui';

/** ערי מבחן — new design. Same endpoints as ExamCitiesList (/exam-cities). */
const emptyForm = () => ({ name: '', ivrId: '', promptCode: '', order: '', isActive: true });

export default function ExamCitiesListNext() {
  const toast = useCkToast();
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/exam-cities');
      setCities(data.data || []);
    } catch (e) {
      setError(apiError(e, 'שגיאה בטעינת ערים'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadData(); }, [loadData]);

  const openCreate = () => { setEditId(null); setForm(emptyForm()); setDialogOpen(true); };
  useQuickCreate(() => openCreate());
  const openEdit = (c) => {
    setEditId(c._id);
    setForm({ name: c.name || '', ivrId: c.ivrId ?? '', promptCode: c.promptCode || '', order: c.order ?? '', isActive: c.isActive !== false });
    setDialogOpen(true);
  };
  const save = async () => {
    if (!String(form.name || '').trim()) { toast.error('שם העיר חובה'); return; }
    setSaving(true);
    try {
      const payload = { name: String(form.name).trim(), promptCode: String(form.promptCode || '').trim(), isActive: !!form.isActive };
      if (form.ivrId !== '' && form.ivrId != null) payload.ivrId = Number(form.ivrId);
      if (form.order !== '' && form.order != null) payload.order = Number(form.order);
      if (editId) { await api.put(`/exam-cities/${editId}`, payload); toast.success('העיר עודכנה'); }
      else { await api.post('/exam-cities', payload); toast.success('העיר נוספה'); }
      setDialogOpen(false);
      loadData();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה'));
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    try {
      await api.delete(`/exam-cities/${deleteTarget._id}`);
      toast.success('העיר נמחקה');
      loadData();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה'));
    } finally {
      setDeleteTarget(null);
    }
  };
  const toggleActive = async (c) => {
    try {
      await api.put(`/exam-cities/${c._id}`, { isActive: !c.isActive });
      loadData();
    } catch (e) {
      toast.error(apiError(e, 'שגיאה'));
    }
  };

  return (
    <>
      <PageHeader title="ערי מבחן" sub="הרשימה משפיעה על פורטל ההרשמות ועל תפריט הטלפון. מספר IVR הוא המספר שמקישים בטלפון." primary={{ label: 'הוספת עיר', onClick: openCreate }} />
      <DataTable
        mode="client"
        rows={cities}
        rowKey="_id"
        loading={loading}
        error={error}
        onRetry={loadData}
        onRefresh={loadData}
        selectable={false}
        storageKey="bchinot-exam-cities"
        defaultQuery={{ sort: { key: 'order', dir: 1 } }}
        exportFilename="ערי מבחן"
        emptyTitle="אין ערים"
        emptyText="לחצו על «הוספת עיר»"
        onRowClick={openEdit}
        rowActions={(c) => [
          { key: 'edit', icon: 'edit', label: 'עריכה', onClick: () => openEdit(c) },
          { key: 'del', icon: 'trash', label: 'מחיקה', onClick: () => setDeleteTarget(c) },
        ]}
        columns={[
          { key: 'ivrId', label: 'מספר IVR', type: 'number', width: 100, render: (c) => <span className="nx-num">{c.ivrId}</span> },
          { key: 'name', label: 'עיר', render: (c) => <b>{c.name}</b> },
          { key: 'order', label: 'סדר', type: 'number' },
          { key: 'promptCode', label: 'קריינות', getValue: (c) => c.promptCode || 'TTS', render: (c) => (c.promptCode ? <span className="nx-code">{c.promptCode}</span> : <Tag>TTS</Tag>) },
          {
            key: 'isActive',
            label: 'סטטוס',
            type: 'bool',
            getValue: (c) => !!c.isActive,
            render: (c) => (
              <button type="button" className="ck-link" title="לחיצה מחליפה מצב" onClick={(e) => { e.stopPropagation(); toggleActive(c); }}>
                <Tag tone={c.isActive ? 'success' : 'neutral'}>{c.isActive ? 'פעילה' : 'כבויה'}</Tag>
              </button>
            ),
          },
        ]}
      />

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editId ? 'עריכת עיר' : 'הוספת עיר'}
        icon="flag"
        size="md"
        footer={(
          <>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>ביטול</Button>
            <Button variant="primary" disabled={saving} onClick={save}>{saving ? 'שומר…' : 'שמירה'}</Button>
          </>
        )}
      >
        <div className="nx-stack">
          <FormGrid cols={2}>
            <Field label="שם העיר *"><TextInput value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-autofocus /></Field>
            <Field label="מספר בטלפון (IVR)" hint={editId ? '' : 'ריק = מספר הבא אוטומטית'}><TextInput type="number" ltr value={form.ivrId} onChange={(e) => setForm((f) => ({ ...f, ivrId: e.target.value }))} /></Field>
            <Field label="קוד קריינות (אופציונלי)" hint="למשל 045 — בלי קוד יושמע TTS"><TextInput ltr value={form.promptCode} onChange={(e) => setForm((f) => ({ ...f, promptCode: e.target.value }))} /></Field>
            <Field label="סדר תצוגה"><TextInput type="number" ltr value={form.order} onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))} /></Field>
          </FormGrid>
          <Switch label="פעילה" checked={!!form.isActive} onChange={(v) => setForm((f) => ({ ...f, isActive: v }))} />
        </div>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={remove}
        title="מחיקת עיר"
        message={<p className="ck-confirm__msg">{`למחוק את «${deleteTarget?.name || ''}»? אם יש הרשמות עם מוקד זה — המחיקה תיחסם.`}</p>}
        confirmLabel="מחיקה"
      />
    </>
  );
}
